import { useState } from 'react'
import type { Appliance, RateTier, Season } from '../../types/electricity'
import { appliances } from '../../data/appliances'
import { RATES } from '../../data/rates'
import { calcCostPerHour } from '../../utils/electricity'
import { ApplianceCard } from './ApplianceCard'
import { useLanguage } from '../../i18n/LanguageContext'

export function ApplianceCalculator({ tier, season }: { tier: RateTier; season: Season }) {
  const [selected, setSelected] = useState<Appliance | null>(null)
  const { t } = useLanguage()

  const seasonRates = RATES[season]
  const isPeak = tier === 'peak'
  const currentRate = isPeak ? seasonRates.peak : seasonRates.offpeak
  const otherRate = isPeak ? seasonRates.offpeak : seasonRates.peak

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        {appliances.map((a) => (
          <ApplianceCard
            key={a.id}
            appliance={a}
            selected={selected?.id === a.id}
            onSelect={(app) => setSelected(selected?.id === app.id ? null : app)}
          />
        ))}
      </div>

      {selected && (() => {
        const costNow = calcCostPerHour(selected.watts, currentRate)
        const costOther = calcCostPerHour(selected.watts, otherRate)
        const saving = Math.abs(costOther - costNow)
        const yearlySaving = saving * selected.dailyHours * 365
        const name = t(`appliances.${selected.id}` as 'appliances.ac') as string
        const otherLabel = isPeak ? t('calculator.costOffpeak') as string : t('calculator.costPeak') as string

        return (
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{selected.emoji}</span>
                <h3 className="font-heading font-bold text-white text-base">{name}</h3>
                <span className="text-slate-500 text-xs">({selected.watts}W)</span>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-1">{t('calculator.costNow') as string}</p>
                <p className={`font-heading font-bold text-lg ${isPeak ? 'text-red-400' : 'text-emerald-400'}`}>
                  {costNow.toFixed(2)} ₪
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-1">{otherLabel}</p>
                <p className={`font-heading font-bold text-lg ${isPeak ? 'text-emerald-400' : 'text-red-400'}`}>
                  {costOther.toFixed(2)} ₪
                </p>
              </div>
            </div>

            <div className={`mt-3 text-center rounded-lg p-2 ${isPeak ? 'bg-yellow-500/10' : 'bg-emerald-500/10'}`}>
              <p className={`text-sm font-bold ${isPeak ? 'text-yellow-400' : 'text-emerald-400'}`}>
                {isPeak ? t('calculator.waitAndSave') as string : t('calculator.runNow') as string}
              </p>
              {selected.deferrable && yearlySaving >= 1 && (
                <p className="text-xs text-slate-300 mt-1">
                  {t('calculator.yearlySaving') as string} {Math.round(yearlySaving)} {t('calculator.perYear') as string}
                </p>
              )}
              {selected.deferrable && season === 'transition' && (
                <p className="text-xs text-amber-400/70 mt-1">
                  {t('calculator.transitionNote') as string}
                </p>
              )}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
